import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import ScienceScoringManagementListing from '../../components/ScientificManagement/ScienceScoringManagement/ScienceScoringManagementListing';
import ScienceScoringManagementForm from '../../components/ScientificManagement/ScienceScoringManagement/ScienceScoringManagementForm';
import ScienceScoringManagementReferenceForm from '../../components/ScientificManagement/ScienceScoringManagement/ScienceScoringManagementReferenceForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';
import { downloadExcel } from '../../utils/excelUtils';
import * as XLSX from 'xlsx';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, ...rest }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();
                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };
                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            {...rest}
        />
    );
};

const ScienceScoringManagement = () => {
    const toast = useRef(null);

    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [modalMode, setModalMode] = useState(null);
    const isFormOpen = modalMode !== null;
    const [editItem, setEditItem] = useState(null);

    const [isRefModalOpen, setIsRefModalOpen] = useState(false);

    const [isEditRequestModalOpen, setIsEditRequestModalOpen] = useState(false);
    const [selectedDeclaration, setSelectedDeclaration] = useState(null);
    const [editRequestContent, setEditRequestContent] = useState('');

    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [rejectItem, setRejectItem] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);

    const [filters, setFilters] = useState({
        department: '', authorId: null, status: '', fromDate: null, toDate: null
    });

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [previewExportData, setPreviewExportData] = useState(null);
    const [exportFilters, setExportFilters] = useState({
        fromDate: null, toDate: null, scoringContent: '', department: '', userId: ''
    });

    const [isDesktop, setIsDesktop] = useState(true);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth > 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
    const userRoleName = useMemo(() => (currentUser.RoleName || '').toLowerCase(), [currentUser.RoleName]);

    const isManager = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa'),
        [userRoleName, currentUser.RoleId]
    );

    const canModify = useMemo(() =>
        currentUser.RoleId === 1 || userRoleName === 'quản trị viên' ||
        ((currentUser.RoleId === 11 || userRoleName.includes('quản lý khoa')) && currentUser.IsDelegatedAdmin === true),
        [userRoleName, currentUser.RoleId, currentUser.IsDelegatedAdmin]
    );

    const authHeaders = useMemo(() => ({
        'X-User-Role-Id': currentUser.RoleId ? currentUser.RoleId.toString() : '',
        'X-User-Dept-Id': currentUser.DepartmentId ? currentUser.DepartmentId.toString() : '',
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
    }), [currentUser]);

    const fetchDropdowns = useCallback(async () => {
        try {
            const [deptRes, userRes] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_BASE_URL}/department`, { headers: authHeaders }),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/user?mode=selection`, { headers: authHeaders })
            ]);
            if (deptRes.ok) setDepartments(await deptRes.json());
            if (userRes.ok) setUsers(await userRes.json());
        } catch (error) {
            console.error(error);
        }
    }, [authHeaders]);

    const fetchData = useCallback(async (isBackground = false) => {
        if (!currentUser?.Id) return;
        if (!isBackground) setIsLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?userId=${currentUser.Id}&isAdmin=${isManager}`,
                { method: 'GET', headers: authHeaders }
            );
            if (response.ok) {
                const result = await response.json();
                setData(result);
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể tải dữ liệu!', life: 3000 });
        } finally {
            if (!isBackground) setIsLoading(false);
        }
    }, [currentUser?.Id, isManager, authHeaders]);

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, [fetchData, fetchDropdowns]);

    const filterUserOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            return {
                label: fullName,
                searchKey: superCleanString(rawSearchStr),
                value: u.Id,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        let _filteredAuthors;

        if (!query.trim().length) {
            _filteredAuthors = [...filterUserOptions];
        } else {
            _filteredAuthors = filterUserOptions.filter((user) => {
                return user.searchKey.includes(query);
            });
        }
        setFilteredAuthors(_filteredAuthors);
    };

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const getScoringContent = useCallback((item) => {
        const val = item.ScoringContent || item.ScoringType || item.ContentType || item.ContentName || item.Type;
        if (!val || val === 'All' || val === 'Tất cả') return 'Tất cả công trình';
        return val;
    }, []);

    const uniqueContents = useMemo(() => {
        const contents = data.map(item => getScoringContent(item)).filter(Boolean);
        return [...new Set(contents)].sort();
    }, [data, getScoringContent]);

    const exportUserOptions = useMemo(() => {
        let allowedDeptNames = [];
        if (exportFilters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === exportFilters.department.trim());
            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());
                const findChildren = (parentId) => {
                    departments.filter(d => d.ParentId === parentId).forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };
                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(exportFilters.department.trim());
            }
        }

        const userMap = new Map();
        data.forEach(item => {
            if (exportFilters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) return;
            }

            if (item.UserId && !userMap.has(item.UserId)) {
                const labelStr = `${item.FullName || ''} - ${item.Email || ''}`.trim();
                userMap.set(item.UserId, {
                    label: labelStr,
                    searchKey: superCleanString(labelStr),
                    value: item.UserId
                });
            }
        });
        return Array.from(userMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [data, exportFilters.department, departments]);

    const filteredData = useMemo(() => {
        let allowedDeptNames = [];
        if (filters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === filters.department.trim());

            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());
                const findChildren = (parentId) => {
                    const children = departments.filter(d => d.ParentId === parentId);
                    children.forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };
                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(filters.department.trim());
            }
        }

        return data.filter(item => {
            if (filters.status && item.Status !== filters.status) return false;

            if (filters.department) {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                if (!allowedDeptNames.includes(itemDeptName)) {
                    return false;
                }
            }

            if (filters.authorId) {
                if (item.UserId?.toString() !== filters.authorId.toString()) {
                    return false;
                }
            }

            if (filters.fromDate) {
                const filterFrom = new Date(filters.fromDate);
                filterFrom.setHours(0, 0, 0, 0);

                const itemFromDate = item.FromDate ? new Date(item.FromDate) : null;
                if (!itemFromDate) return false;
                itemFromDate.setHours(0, 0, 0, 0);

                if (itemFromDate.getTime() !== filterFrom.getTime()) return false;
            }

            if (filters.toDate) {
                const filterTo = new Date(filters.toDate);
                filterTo.setHours(0, 0, 0, 0);

                const itemToDate = item.ToDate ? new Date(item.ToDate) : null;
                if (!itemToDate) return false;
                itemToDate.setHours(0, 0, 0, 0);

                if (itemToDate.getTime() !== filterTo.getTime()) return false;
            }

            return true;
        });
    }, [data, filters, departments]);

    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép sửa dữ liệu!', life: 3000 });
        }

        if (item.Status === 'Đã duyệt') {
            setEditItem(item);
            setModalMode('view');
            return;
        }

        setEditItem(item);
        setModalMode('edit');
    }, [canModify]);

    const handleViewDetail = useCallback((item) => {
        setEditItem(item);
        setModalMode('view');
    }, []);

    const handleStatusChange = useCallback(async (id, newStatus, note = '') => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt/từ chối!', life: 3000 });
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ id: id.toString(), status: newStatus, note: note })
            });
            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã cập nhật trạng thái!' });
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Thao tác thất bại!' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData]);

    const handleBulkUpdateStatus = async (ids, newStatus) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền phê duyệt!', life: 3000 });
        }

        if (newStatus === 'Đã duyệt') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các bảng kê đã duyệt để thao tác!', life: 4000 });
            }
        } else if (newStatus === 'Đang chỉnh sửa') {
            const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status !== 'Đã duyệt');
            if (invalidItems.length > 0) {
                return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Chỉ có thể hủy đối với bảng kê có trạng thái "Đã duyệt"!', life: 4000 });
            }
        }

        setIsLoading(true);
        try {
            const promises = ids.map(id =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                    method: 'PATCH',
                    headers: {
                        ...authHeaders,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ id: id.toString(), status: newStatus, note: '' })
                })
            );

            const results = await Promise.all(promises);
            const allOk = results.every(res => res.ok);

            if (allOk) {
                toast.current?.show({
                    severity: 'success', summary: 'Thành công',
                    detail: newStatus === 'Đã duyệt' ? `Đã phê duyệt ${ids.length} mục!` : `Đã hủy duyệt ${ids.length} mục!`, life: 3000
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: "Một số thao tác thất bại!", life: 3000 });
            }
            fetchData(true);
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!', life: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenRequestEdit = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền yêu cầu chỉnh sửa!', life: 3000 });
        }
        setSelectedDeclaration(item);
        setEditRequestContent('');
        setIsEditRequestModalOpen(true);
    }, [canModify]);

    const handleSendEditRequest = async (e) => {
        e.preventDefault();
        if (!editRequestContent.trim()) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập nội dung yêu cầu!', life: 3000 });
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?userId=${currentUser.Id}&isAdmin=${isManager}`, {
                method: 'PATCH',
                headers: {
                    ...authHeaders,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: selectedDeclaration.Id.toString(),
                    status: 'Yêu cầu chỉnh sửa',
                    note: editRequestContent
                })
            });
            if (response.ok) {
                toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã gửi Email cho giảng viên!' });
                setIsEditRequestModalOpen(false);
                fetchData(true);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Gửi yêu cầu thất bại!' });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối máy chủ!' });
        }
    };

    const handleOpenRejectModal = useCallback((item) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn không có quyền từ chối!', life: 3000 });
        }
        setRejectItem(item);
        setRejectReason('');
        setIsRejectModalOpen(true);
    }, [canModify]);

    const handleConfirmReject = async (e) => {
        e.preventDefault();
        if (!rejectReason.trim()) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng nhập lý do từ chối!', life: 3000 });
        }
        await handleStatusChange(rejectItem.Id, 'Từ chối', rejectReason);
        setIsRejectModalOpen(false);
    };

    const handleDelete = useCallback((id) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa bản ghi này? Bảng kê của giảng viên cũng sẽ bị xóa.',
            accept: async () => {
                try {
                    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?id=${id}&userId=${currentUser.Id}&isAdmin=${isManager}`, {
                        method: 'DELETE',
                        headers: authHeaders
                    });
                    if (res.ok) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xóa!' });
                        fetchData(true);
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Xóa thất bại!' });
                    }
                } catch (e) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi máy chủ!' });
                }
            }
        });
    }, [canModify, currentUser.Id, isManager, authHeaders, fetchData, confirmDeleteDialog]);

    const handleBulkDelete = (ids) => {
        if (!canModify) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Tài khoản của bạn chỉ có quyền xem, không được phép xóa!', life: 3000 });
        }
        const invalidItems = data.filter(d => ids.includes(d.Id) && d.Status === 'Đã duyệt');
        if (invalidItems.length > 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Vui lòng bỏ chọn các bảng kê đã duyệt khỏi danh sách cần xóa!', life: 4000 });
        }

        confirmDeleteDialog({
            header: 'Xác nhận xóa hàng loạt',
            message: `Bạn có chắc chắn muốn xóa ${ids.length} bảng kê tính điểm đã chọn? Bảng kê của giảng viên cũng sẽ bị xóa.`,
            accept: async () => {
                setIsLoading(true);
                try {
                    const promises = ids.map(id =>
                        fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?id=${id}&userId=${currentUser.Id}&isAdmin=${isManager}`, {
                            method: 'DELETE',
                            headers: authHeaders
                        })
                    );

                    const results = await Promise.all(promises);
                    const allOk = results.every(res => res.ok);

                    if (allOk) {
                        toast.current?.show({ severity: 'success', summary: 'Thành công', detail: `Đã xóa ${ids.length} bản ghi!` });
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Một số bản ghi xóa thất bại!' });
                    }
                    fetchData(true);
                } catch (error) {
                    toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi kết nối!' });
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const closeModal = useCallback(() => {
        setModalMode(null);
        setEditItem(null);
    }, []);

    const executeExport = useCallback(async (dataToExport, fileSuffix) => {
        if (dataToExport.length === 0) {
            return toast.current?.show({ severity: 'warn', summary: 'Cảnh báo', detail: 'Không có dữ liệu để xuất!', life: 3000 });
        }

        toast.current?.show({ severity: 'info', summary: 'Đang tổng hợp', detail: 'Đang tải chi tiết cho từng giảng viên. Quá trình này có thể mất một lúc...', life: 10000 });

        try {
            const [journalTypes, allArticles, allTopics, allBooks, allOthers] = await Promise.all([
                fetch(`${process.env.REACT_APP_API_BASE_URL}/journaltype`, { headers: authHeaders }).then(res => res.ok ? res.json() : []).catch(() => []),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/scientificarticlemngt?isAdmin=true`, { headers: authHeaders }).then(res => res.ok ? res.json() : []).catch(() => []),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/scientifictopic?isAdmin=true`, { headers: authHeaders }).then(res => res.ok ? res.json() : []).catch(() => []),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/bookpublication?isAdmin=true`, { headers: authHeaders }).then(res => res.ok ? res.json() : []).catch(() => []),
                fetch(`${process.env.REACT_APP_API_BASE_URL}/othersciencedeclaration?isAdmin=true`, { headers: authHeaders }).then(res => res.ok ? res.json() : []).catch(() => [])
            ]);

            const userDetailsPromises = dataToExport.map(item =>
                fetch(`${process.env.REACT_APP_API_BASE_URL}/sciencescoringmanagement?action=calculatescore`, {
                    method: 'POST',
                    headers: { ...authHeaders, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        UserId: item.UserId,
                        ScoringContent: item.ScoringContent || 'All',
                        ItemStatusFilter: item.ItemStatusFilter || 'All',
                        FromDate: item.FromDate?.substring(0, 10),
                        ToDate: item.ToDate?.substring(0, 10),
                        Coefficient: item.Coefficient || 1
                    })
                }).then(res => res.ok ? res.json() : null).catch(() => null)
            );
            const userDetailsResults = await Promise.all(userDetailsPromises);

            const isUserInMembers = (membersJson, singleUserId, targetUserId) => {
                if (singleUserId && singleUserId.toString() === targetUserId.toString()) return true;
                try {
                    const members = JSON.parse(membersJson || '[]');
                    return members.some(m => {
                        const mId = m.UserId || m.userId || m.Id || m.id;
                        return mId?.toString() === targetUserId.toString();
                    });
                } catch { return false; }
            };

            const formattedData = dataToExport.map((item, index) => {
                const score = item.AchievedScore ? Number(item.AchievedScore).toFixed(1).replace(/\.?0+$/, '') : 0;
                const hours = item.ConvertedHours ? Number(item.ConvertedHours).toFixed(1).replace(/\.?0+$/, '') : 0;
                const targetUserId = item.UserId;

                let articleCount = 0, topicCount = 0, bookCount = 0, otherCount = 0;
                let jtCounts = {};
                journalTypes.forEach(jt => jtCounts[jt.Name] = 0);

                const itemFromDate = item.FromDate ? new Date(item.FromDate) : null;
                if (itemFromDate) itemFromDate.setHours(0, 0, 0, 0);

                const itemToDate = item.ToDate ? new Date(item.ToDate) : null;
                if (itemToDate) itemToDate.setHours(23, 59, 59, 999);

                const isDateInRange = (obj) => {
                    const dateStr = obj.PublicationDate || obj.publicationDate || obj.AcceptanceDate || obj.acceptanceDate || obj.PublishDate || obj.publishDate || obj.ApplyDate || obj.applyDate || obj.Date || obj.date;
                    if (!dateStr) return false;
                    const objDate = new Date(dateStr);
                    if (itemFromDate && objDate < itemFromDate) return false;
                    if (itemToDate && objDate > itemToDate) return false;
                    return true;
                };

                allArticles.forEach(article => {
                    if (article.Status !== 'Đã duyệt') return;
                    if (!isDateInRange(article)) return;
                    if (isUserInMembers(article.MembersJSON, article.UserId, targetUserId)) {
                        articleCount++;
                        if (article.JournalType && jtCounts[article.JournalType] !== undefined) jtCounts[article.JournalType]++;
                    }
                });

                allTopics.forEach(topic => {
                    if (topic.Status !== 'Đã duyệt') return;
                    if (!isDateInRange(topic)) return;
                    if (isUserInMembers(topic.MembersJSON, topic.UserId, targetUserId)) topicCount++;
                });

                allBooks.forEach(book => {
                    if (book.Status !== 'Đã duyệt') return;
                    if (!isDateInRange(book)) return;
                    if (isUserInMembers(book.MembersJSON, book.UserId, targetUserId)) bookCount++;
                });

                allOthers.forEach(other => {
                    if (other.Status !== 'Đã duyệt') return;
                    if (!isDateInRange(other)) return;
                    if (other.UserId?.toString() === targetUserId?.toString()) {
                        otherCount += (other.Quantity ? parseInt(other.Quantity) : 1);
                    }
                });

                const rowData = {
                    'STT': index + 1,
                    'Giảng viên': item.FullName || '',
                    'Email': item.Email || '',
                    'Đơn vị': item.DepartmentName || item.DepartmentId || '',
                    'Chức danh': item.JobTitleName || item.JobTitle || '',
                    'Học vị': item.DegreeName || item.Degree || '',
                    'Học hàm': item.AcademicRank || '',
                    'Năm': item.ToDate ? new Date(item.ToDate).getFullYear() : (item.FromDate ? new Date(item.FromDate).getFullYear() : ''),
                    'Nội dung tính điểm': getScoringContent(item)
                };

                journalTypes.forEach(jt => { rowData[jt.Name] = jtCounts[jt.Name]; });
                rowData['Bài báo - Tham luận'] = articleCount;
                rowData['Đề tài'] = topicCount;
                rowData['Sách - Giáo trình'] = bookCount;
                rowData['Nội dung NCKH khác'] = otherCount;
                rowData['Định mức chuẩn'] = item.StandardHours || item.StandardNorm || 0;
                rowData['Tỉ lệ giảm (%)'] = item.ReductionPercentage || item.ReductionRate || 0;
                rowData['Cần thực hiện'] = Math.round(item.RequiredHours || 0);
                rowData['Thực hiện được (Điểm / Giờ)'] = `${score} / ${hours}`;
                rowData['Hệ số (A)'] = item.Coefficient || 1;
                rowData['Năm trước'] = item.PreviousYearHours || 0;
                rowData['Chênh lệch'] = item.DifferenceHours || 0;
                rowData['Từ ngày'] = item.FromDate ? new Date(item.FromDate).toLocaleDateString('vi-VN') : '';
                rowData['Đến ngày'] = item.ToDate ? new Date(item.ToDate).toLocaleDateString('vi-VN') : '';
                rowData['Ngày cập nhật'] = item.UpdatedDate || '';
                rowData['Ghi chú'] = item.Note || '';
                rowData['Trạng thái'] = item.Status || 'Đang chỉnh sửa';
                rowData['Cơ chế áp dụng'] = item.AppliedConfigVersionName || '';

                return rowData;
            });

            const totalScoreSum = dataToExport.reduce((sum, item) => sum + (parseFloat(item.AchievedScore) || 0), 0);

            const summaryRow = {
                'STT': '', 'Giảng viên': 'TỔNG CỘNG:', 'Email': `${dataToExport.length} Bản kê`,
                'Đơn vị': '', 'Chức danh': '', 'Học vị': '', 'Học hàm': '',
                'Năm': '', 'Nội dung tính điểm': ''
            };

            journalTypes.forEach(jt => { summaryRow[jt.Name] = formattedData.reduce((sum, row) => sum + (row[jt.Name] || 0), 0); });
            summaryRow['Bài báo - Tham luận'] = formattedData.reduce((sum, row) => sum + (row['Bài báo - Tham luận'] || 0), 0);
            summaryRow['Đề tài'] = formattedData.reduce((sum, row) => sum + (row['Đề tài'] || 0), 0);
            summaryRow['Sách - Giáo trình'] = formattedData.reduce((sum, row) => sum + (row['Sách - Giáo trình'] || 0), 0);
            summaryRow['Nội dung NCKH khác'] = formattedData.reduce((sum, row) => sum + (row['Nội dung NCKH khác'] || 0), 0);
            summaryRow['Định mức chuẩn'] = ''; summaryRow['Tỉ lệ giảm (%)'] = ''; summaryRow['Cần thực hiện'] = '';
            summaryRow['Thực hiện được (Điểm / Giờ)'] = `${totalScoreSum} (Tổng điểm)`;
            summaryRow['Hệ số (A)'] = ''; summaryRow['Năm trước'] = ''; summaryRow['Chênh lệch'] = '';
            summaryRow['Từ ngày'] = ''; summaryRow['Đến ngày'] = ''; summaryRow['Ngày cập nhật'] = '';
            summaryRow['Ghi chú'] = ''; summaryRow['Trạng thái'] = ''; summaryRow['Cơ chế áp dụng'] = '';

            const colWidths = [
                { wch: 5 }, { wch: 25 }, { wch: 25 }, { wch: 20 }, { wch: 15 },
                { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 30 }
            ];
            journalTypes.forEach(() => colWidths.push({ wch: 18 }));
            colWidths.push({ wch: 22 }, { wch: 15 }, { wch: 20 }, { wch: 22 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 30 });

            const workbook = XLSX.utils.book_new();

            const summaryWorksheet = XLSX.utils.json_to_sheet([...formattedData, summaryRow]);
            summaryWorksheet['!cols'] = colWidths;
            XLSX.utils.book_append_sheet(workbook, summaryWorksheet, "Tổng hợp");

            dataToExport.forEach((item, index) => {
                const resData = userDetailsResults[index];
                if (!resData || !resData.Summary) return;

                const summary = resData.Summary;
                const details = resData.Details || [];

                const aoa = [
                    ['KẾT QUẢ TÍNH ĐIỂM KHOA HỌC CHI TIẾT'],
                    ['Giảng viên:', summary.LecturerName || item.FullName, '', 'Đơn vị:', summary.DepartmentName || item.DepartmentName],
                    [],
                    ['1. TỔNG HỢP SỐ GIỜ NCKH ĐẠT ĐƯỢC'],
                    ['Chức danh', 'Định mức chuẩn (giờ)', 'Tỉ lệ giảm (%)', 'Cần đạt (giờ)', 'Tổng điểm (điểm)', 'Hệ số', 'Năm trước (giờ)', 'Giờ quy đổi (giờ)', 'Chênh lệch (giờ)'],
                    [
                        summary.JobTitle,
                        summary.StandardHours,
                        Math.round(Number(summary.ReductionPercentage) * 100) / 100,
                        Number(summary.RequiredHours).toFixed(2),
                        Number(summary.AchievedScore).toFixed(2),
                        summary.Coefficient,
                        summary.PreviousYearHours || 0,
                        Number(summary.ConvertedHours).toFixed(2),
                        Number(summary.DifferenceHours).toFixed(2)
                    ],
                    [],
                    ['2. CHI TIẾT CÔNG TRÌNH'],
                    ['STT', 'Loại', 'Tiêu đề', 'Nơi đăng/Cấp', 'Điểm tạp chí', 'Ngày NT/XB', 'Tác giả', 'Vai trò', 'Cơ chế áp dụng', 'Số điểm']
                ];

                if (details.length > 0) {
                    details.forEach((d, idx) => {
                        aoa.push([
                            idx + 1,
                            d.Type,
                            d.Title,
                            d.Journal,
                            d.BaseScore ?? '-',
                            d.PublishDate,
                            d.Authors,
                            d.Role || '-',
                            d.VersionName || 'Mặc định',
                            Number(d.Score).toFixed(2)
                        ]);
                    });
                } else {
                    aoa.push(['Không có công trình nào trong khoảng thời gian này']);
                }

                const ws = XLSX.utils.aoa_to_sheet(aoa);
                ws['!cols'] = [
                    { wch: 5 }, { wch: 15 }, { wch: 45 }, { wch: 25 }, { wch: 12 },
                    { wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 18 }, { wch: 10 }
                ];

                let sheetName = (item.FullName || `User_${item.UserId}`).replace(/[\\/?*\[\]:]/g, '').substring(0, 31);
                let counter = 1;
                let finalSheetName = sheetName;
                while (workbook.SheetNames.includes(finalSheetName)) {
                    finalSheetName = `${sheetName.substring(0, 27)}_${counter}`;
                    counter++;
                }
                XLSX.utils.book_append_sheet(workbook, ws, finalSheetName);
            });

            XLSX.writeFile(workbook, `QuanLyTinhDiemKH_${fileSuffix}.xlsx`);
            toast.current?.show({ severity: 'success', summary: 'Thành công', detail: 'Đã xuất file Excel thành công!', life: 3000 });

        } catch (error) {
            console.error("Lỗi xuất Excel:", error);
            toast.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Lỗi khi kết nối các module NCKH!', life: 3000 });
        }

    }, [getScoringContent, authHeaders]);

    const handleExportCurrent = () => {
        executeExport(filteredData, 'HienHanh');
        setExportDropdownOpen(false);
    };

    const getFilteredExportData = useCallback(() => {
        let allowedDeptNames = [];
        if (exportFilters.department) {
            const selectedDept = departments.find(d => d.Name.trim() === exportFilters.department.trim());
            if (selectedDept) {
                allowedDeptNames.push(selectedDept.Name.trim());
                const findChildren = (parentId) => {
                    departments.filter(d => d.ParentId === parentId).forEach(child => {
                        allowedDeptNames.push(child.Name.trim());
                        findChildren(child.Id);
                    });
                };
                findChildren(selectedDept.Id);
            } else {
                allowedDeptNames.push(exportFilters.department.trim());
            }
        }

        let result = [...data];

        if (exportFilters.fromDate) {
            const filterFrom = new Date(exportFilters.fromDate);
            filterFrom.setHours(0, 0, 0, 0);
            result = result.filter(item => item.FromDate && new Date(item.FromDate) >= filterFrom);
        }
        if (exportFilters.toDate) {
            const filterTo = new Date(exportFilters.toDate);
            filterTo.setHours(23, 59, 59, 999);
            result = result.filter(item => item.ToDate && new Date(item.ToDate) <= filterTo);
        }

        if (exportFilters.department) {
            result = result.filter(item => {
                const itemDeptName = item.DepartmentName ? item.DepartmentName.trim() : "";
                return allowedDeptNames.includes(itemDeptName);
            });
        }

        if (exportFilters.scoringContent) {
            result = result.filter(item => getScoringContent(item) === exportFilters.scoringContent);
        }
        if (exportFilters.userId) {
            result = result.filter(item => item.UserId?.toString() === exportFilters.userId.toString());
        }

        return result;
    }, [data, exportFilters, getScoringContent, departments]);

    const handlePreviewAdvancedFilters = () => {
        setPreviewExportData(getFilteredExportData());
    };

    const handleClearAdvancedFilters = () => {
        setExportFilters({ fromDate: null, toDate: null, scoringContent: '', department: '', userId: '' });
        setPreviewExportData(null);
    };

    const handleExportFromModal = () => {
        executeExport(previewExportData || getFilteredExportData(), 'LocNangCao');
        setIsExportModalOpen(false);
    };

    const renderDeptTree = (parentId = null, level = 0) => {
        const targetNodes = parentId === null
            ? departments.filter(d => !d.ParentId || !departments.some(p => p.Id === d.ParentId))
            : departments.filter(d => d.ParentId === parentId);

        return targetNodes.map(d => (
            <React.Fragment key={`filter-dept-${d.Id}`}>
                <option value={d.Name}>
                    {'\u00A0'.repeat(level * 4)}{level > 0 ? '˪ ' : ''}{d.Name}
                </option>
                {renderDeptTree(d.Id, level + 1)}
            </React.Fragment>
        ));
    };

    return (
        <div className="page-container">
            <style>{`
                .p-datepicker, 
                .p-dropdown-panel {
                    z-index: 9999 !important; 
                }
            `}</style>

            <Toast ref={toast} position="top-right" baseZIndex={99999} style={{ zIndex: 99999 }} />

            <div className="page-header">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUẢN LÝ ĐIỂM KHOA HỌC</h2>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: isDesktop ? 'row' : 'column', gap: '10px', marginBottom: '20px' }}>
                {canModify && (
                    <>
                        <button
                            className="btn-add-new"
                            onClick={() => { setEditItem(null); setModalMode('edit'); }}
                            style={{ margin: 0, width: isDesktop ? 'auto' : '100%' }}
                        >
                            <i className="fa-solid fa-plus"></i> Tính điểm
                        </button>

                        <button
                            className="btn-add-new"
                            onClick={() => setIsRefModalOpen(true)}
                            style={{ backgroundColor: '#f39c12', margin: 0, width: isDesktop ? 'auto' : '100%' }}
                        >
                            <i className="fa-solid fa-calculator"></i> Tính điểm tham khảo
                        </button>
                    </>
                )}

                <div style={{ position: 'relative', display: isDesktop ? 'inline-block' : 'block', width: isDesktop ? 'auto' : '100%' }}>
                    <button
                        className="btn-add-new"
                        style={{ backgroundColor: '#1a9953', margin: 0, width: '100%' }}
                        onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                    >
                        <i className="fa-solid fa-file-excel"></i> Xuất Excel <i className="fa-solid fa-caret-down" style={{ marginLeft: '5px' }}></i>
                    </button>

                    {exportDropdownOpen && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setExportDropdownOpen(false)}></div>
                            <div className="custom-dropdown-menu">
                                <div className="custom-dropdown-item" onClick={handleExportCurrent}>
                                    <i className="fa-solid fa-list-check" style={{ color: '#1a9953', width: '20px', fontSize: '16px' }}></i>
                                    Xuất toàn bộ dữ liệu
                                </div>
                                <div className="custom-dropdown-divider"></div>
                                <div className="custom-dropdown-item" onClick={() => {
                                    setIsExportModalOpen(true);
                                    setExportDropdownOpen(false);
                                    setPreviewExportData(null);
                                }}>
                                    <i className="fa-solid fa-filter" style={{ color: '#f39c12', width: '20px', fontSize: '16px' }}></i>
                                    Lọc dữ liệu nâng cao
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                <p style={{ margin: '0 0 15px 0', fontWeight: 'bold', color: '#555' }}>DANH SÁCH BẢNG KÊ TÍNH GIỜ</p>

                <div style={{
                    display: isDesktop ? 'grid' : 'flex',
                    gridTemplateColumns: isDesktop ? 'repeat(4, 1fr)' : 'none',
                    flexWrap: isDesktop ? 'nowrap' : 'wrap',
                    gap: '15px'
                }}>
                    <select name="department" className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filters.department} onChange={handleFilterChange}>
                        <option value="">Chọn đơn vị</option>
                        {renderDeptTree()}
                    </select>

                    <select name="status" className="form-input" style={{ flex: isDesktop ? 'none' : '1 1 200px' }} value={filters.status} onChange={handleFilterChange}>
                        <option value="">Trạng thái</option>
                        <option value="Đang chỉnh sửa">Đang chỉnh sửa</option>
                        <option value="Đã gửi duyệt">Đã gửi duyệt</option>
                        <option value="Đã duyệt">Đã duyệt</option>
                        <option value="Từ chối">Từ chối</option>
                        <option value="Yêu cầu chỉnh sửa">Yêu cầu chỉnh sửa</option>
                        <option value="Tham khảo">Tham khảo</option>
                    </select>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="fromDate"
                            value={filters.fromDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))}
                            placeholder="Từ ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ width: '100%' }}>
                        <Calendar
                            id="toDate"
                            value={filters.toDate}
                            onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))}
                            placeholder="Đến ngày"
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="custom-prime-calendar"
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ paddingLeft: '12px' }}
                        />
                    </div>

                    <div style={{ flex: isDesktop ? 'none' : '1 1 200px', width: '100%', gridColumn: isDesktop ? 'span 2' : 'none' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                setSelectedAuthor(e.value);
                                if (e.value && e.value.value) {
                                    setFilters(prev => ({ ...prev, authorId: e.value.value }));
                                } else {
                                    setFilters(prev => ({ ...prev, authorId: null }));
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>
                </div>
            </div>

            <ScienceScoringManagementListing
                data={filteredData}
                filters={filters}
                isLoading={isLoading}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onApprove={(id) => handleStatusChange(id, 'Đã duyệt')}
                onReject={handleOpenRejectModal}
                onCancelApprove={(id) => handleStatusChange(id, 'Đang chỉnh sửa')}
                onRequestEdit={handleOpenRequestEdit}
                onViewDetail={handleViewDetail}
                onBulkApprove={(ids) => handleBulkUpdateStatus(ids, 'Đã duyệt')}
                onBulkCancelApprove={(ids) => handleBulkUpdateStatus(ids, 'Đang chỉnh sửa')}
                onBulkDelete={handleBulkDelete}
            />

            {isFormOpen && (
                <ScienceScoringManagementForm
                    isOpen={isFormOpen}
                    onClose={closeModal}
                    fetchData={fetchData}
                    toast={toast}
                    editItem={editItem}
                    authHeaders={authHeaders}
                    readOnly={modalMode === 'view'}
                    data={data}
                />
            )}

            {isEditRequestModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Yêu cầu chỉnh sửa Bảng kê</h3>
                            <button className="close-btn" onClick={() => setIsEditRequestModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="editRequestForm" onSubmit={handleSendEditRequest}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #3498db' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Nội dung: </strong> Bảng kê tính giờ của Giảng viên {selectedDeclaration?.FullName}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung cần chỉnh sửa (Sẽ gửi qua Email) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="6"
                                        placeholder="Nhập chi tiết yêu cầu bổ sung hoặc sửa lỗi"
                                        value={editRequestContent}
                                        onChange={(e) => setEditRequestContent(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsEditRequestModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="editRequestForm" className="btn-submit" style={{ backgroundColor: '#9b59b6', margin: 0 }}>
                                <i className="fa-solid fa-paper-plane" style={{ marginRight: '8px' }}></i> Gửi Email
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isRejectModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1050 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Từ chối Bảng kê</h3>
                            <button className="close-btn" onClick={() => setIsRejectModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px' }}>
                            <form id="rejectForm" onSubmit={handleConfirmReject}>
                                <div className="form-group" style={{ marginBottom: '15px', backgroundColor: '#fdf2f2', padding: '10px', borderRadius: '5px', borderLeft: '4px solid #e74c3c' }}>
                                    <label style={{ margin: 0, color: '#333', lineHeight: '1.5' }}>
                                        <strong>Nội dung: </strong> Từ chối Bảng kê tính giờ của Giảng viên {rejectItem?.FullName}
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Lý do từ chối (Bắt buộc) *</label>
                                    <textarea
                                        className="form-input"
                                        rows="4"
                                        placeholder="Nhập lý do từ chối bản kê này..."
                                        value={rejectReason}
                                        onChange={(e) => setRejectReason(e.target.value)}
                                        style={{ resize: 'vertical' }}
                                        required
                                    ></textarea>
                                </div>
                            </form>
                        </div>
                        <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-cancel" onClick={() => setIsRejectModalOpen(false)} style={{ margin: 0 }}>Hủy</button>
                            <button type="submit" form="rejectForm" className="btn-submit" style={{ backgroundColor: '#e74c3c', margin: 0 }}>
                                <i className="fa-solid fa-times-circle" style={{ marginRight: '8px' }}></i> Xác nhận từ chối
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isExportModalOpen && (
                <div className="modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="modal-box form-modal-box" style={{ maxWidth: '750px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>Bộ Lọc & Xuất Excel nâng cao</h3>
                            <button className="close-btn" onClick={() => setIsExportModalOpen(false)} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>&times;</button>
                        </div>
                        <div className="modal-body" style={{ padding: '20px', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đơn vị</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.department}
                                        onChange={(e) => setExportFilters({ ...exportFilters, department: e.target.value, userId: '' })}>
                                        <option value="">Toàn bộ đơn vị</option>
                                        {renderDeptTree()}
                                    </select>
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Người dùng (Giảng viên)</label>
                                    <SearchableFocusDropdown
                                        value={exportFilters.userId}
                                        options={exportUserOptions}
                                        onChange={(e) => setExportFilters({ ...exportFilters, userId: e.value })}
                                        placeholder="Toàn bộ giảng viên"
                                        panelUniqueClass="custom-dropdown-panel-sciencescoring-export-advanced-filter"
                                        className="custom-dropdown"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày (Thuộc bản kê)</label>
                                    <Calendar
                                        value={exportFilters.fromDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, fromDate: e.value })}
                                        placeholder="Từ ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        style={{ width: '100%' }}
                                        inputStyle={{ padding: '8px 12px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày (Thuộc bản kê)</label>
                                    <Calendar
                                        value={exportFilters.toDate}
                                        onChange={(e) => setExportFilters({ ...exportFilters, toDate: e.value })}
                                        placeholder="Đến ngày"
                                        dateFormat="dd/mm/yy"
                                        showIcon
                                        style={{ width: '100%' }}
                                        inputStyle={{ padding: '8px 12px' }}
                                        appendTo={document.body}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '15px' }}>
                                <div className="form-group" style={{ flex: '1 1 250px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Nội dung tính điểm</label>
                                    <select className="form-input" style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
                                        value={exportFilters.scoringContent} onChange={(e) => setExportFilters({ ...exportFilters, scoringContent: e.target.value })}>
                                        <option value="">Toàn bộ nội dung</option>
                                        {uniqueContents.map((content, idx) => (
                                            <option key={idx} value={content}>{content}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {previewExportData && (
                                <div style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#1a9953' }}>
                                        <i className="fa-solid fa-table-list"></i> Xem trước dữ liệu: Tìm thấy {previewExportData.length} kết quả
                                    </p>
                                    <div style={{ maxHeight: '250px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                        <table className="custom-table" style={{ width: '100%', minWidth: '500px', fontSize: '13px', margin: 0 }}>
                                            <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th width="50px" style={{ textAlign: 'center' }}>STT</th>
                                                    <th width="150px">Giảng viên</th>
                                                    <th>Nội dung tính điểm</th>
                                                    <th width="160px" style={{ textAlign: 'center' }}>Thực hiện được<br />(Điểm / Giờ)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewExportData.length > 0 ? (
                                                    previewExportData.map((item, idx) => {
                                                        const score = item.AchievedScore ? Number(item.AchievedScore).toFixed(1).replace(/\.?0+$/, '') : 0;
                                                        const hours = item.ConvertedHours ? Number(item.ConvertedHours).toFixed(1).replace(/\.?0+$/, '') : 0;
                                                        return (
                                                            <tr key={item.Id}>
                                                                <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                                <td><div style={{ fontWeight: '500', color: '#333' }}>{item.FullName || '---'}</div></td>
                                                                <td><div style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{getScoringContent(item)}</div></td>
                                                                <td style={{ textAlign: 'center', color: '#0056b3', fontWeight: 'bold' }}>
                                                                    {`${score} / ${hours}`}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                ) : (
                                                    <tr>
                                                        <td colSpan="4" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Không tìm thấy bản ghi nào khớp với điều kiện lọc</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 20px', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '10px' }}>
                            <button type="button" className="btn-cancel" style={{ border: 'none', background: 'none', color: '#e74c3c', margin: 0 }} onClick={handleClearAdvancedFilters}>
                                <i className="fa-solid fa-eraser"></i> Xóa bộ lọc
                            </button>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#3498db', margin: 0 }} onClick={handlePreviewAdvancedFilters}>
                                    <i className="fa-solid fa-magnifying-glass"></i> Lọc & Xem trước
                                </button>
                                <button type="button" className="btn-submit" style={{ backgroundColor: '#1a9953', margin: 0 }} onClick={handleExportFromModal}>
                                    <i className="fa-solid fa-download"></i> Xuất Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isRefModalOpen && (
                <ScienceScoringManagementReferenceForm
                    isOpen={isRefModalOpen}
                    onClose={() => setIsRefModalOpen(false)}
                    toast={toast}
                    data={data}
                    fetchData={fetchData}
                    authHeaders={authHeaders}
                />
            )}
        </div>
    );
};

export default ScienceScoringManagement;